// SPDX-License-Identifier: AGPL-3.0-or-later

import i18nGlobal from '@app/app/I18n';
import {GenericErrorModal} from '@app/features/app/components/alerts/GenericErrorModal';
import Channels from '@app/features/channel/state/Channels';
import type {Guild} from '@app/features/guild/models/Guild';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import ContextMenu from '@app/features/ui/state/ContextMenu';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {GUILD_TEXT_BASED_CHANNEL_TYPES} from '@fluxer/constants/src/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {autorun} from 'mobx';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const REJOIN_NO_ACCESS_TITLE_DESCRIPTOR = msg({
	message: 'This community is for Visionary members',
	comment: 'Title of the error modal shown when a non-Visionary user tries to rejoin a gated Plutonium community.',
});
const REJOIN_NO_ACCESS_MESSAGE_DESCRIPTOR = msg({
	message: 'Only Visionary members can join this community. Your access may have changed since this menu was opened.',
	comment: 'Body of the error modal shown when a non-Visionary user tries to rejoin a gated Plutonium community.',
});
const REJOIN_VISIONARY_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't rejoin the Visionary community",
	comment: 'Title of the generic fallback error modal shown when rejoining the Visionary community fails.',
});
const REJOIN_OPERATORS_FAILED_TITLE_DESCRIPTOR = msg({
	message: "Couldn't rejoin the operators community",
	comment: 'Title of the generic fallback error modal shown when rejoining the operators community fails.',
});
const REJOIN_FAILED_MESSAGE_DESCRIPTOR = msg({
	message: 'Something went wrong while rejoining. Please try again in a moment.',
	comment: 'Body of the generic fallback error modal shown when rejoining a gated Plutonium community fails.',
});

function showRejoinCommunityErrorModal(error: unknown, type: 'visionary' | 'operator'): void {
	const code = failureCode(error);
	const isNoAccess = code === APIErrorCodes.MISSING_ACCESS;
	const fallbackTitle =
		type === 'visionary'
			? i18nGlobal._(REJOIN_VISIONARY_FAILED_TITLE_DESCRIPTOR)
			: i18nGlobal._(REJOIN_OPERATORS_FAILED_TITLE_DESCRIPTOR);
	const title = isNoAccess ? i18nGlobal._(REJOIN_NO_ACCESS_TITLE_DESCRIPTOR) : fallbackTitle;
	const message = isNoAccess
		? i18nGlobal._(REJOIN_NO_ACCESS_MESSAGE_DESCRIPTOR)
		: i18nGlobal._(REJOIN_FAILED_MESSAGE_DESCRIPTOR);
	ModalCommands.push(
		modal(() => (
			<GenericErrorModal
				title={title}
				message={message}
				data-flx="app.plutonium.use-community-actions.rejoin-community.generic-error-modal"
			/>
		)),
	);
}
const OPEN_VISIONARY_COMMUNITY_DESCRIPTOR = msg({
	message: 'Open Visionary community',
	comment: 'Short label in the Plutonium community actions. Keep the tone plain and specific.',
});
const JOIN_VISIONARY_COMMUNITY_DESCRIPTOR = msg({
	message: 'Join Visionary community',
	comment: 'Short label in the Plutonium community actions. Keep the tone plain and specific.',
});
const OPEN_OPERATORS_COMMUNITY_DESCRIPTOR = msg({
	message: 'Open operators community',
	comment: 'Short label in the Plutonium community actions. Keep the tone plain and specific.',
});
const JOIN_OPERATORS_COMMUNITY_DESCRIPTOR = msg({
	message: 'Join operators community',
	comment: 'Short label in the Plutonium community actions. Keep the tone plain and specific.',
});
const logger = new Logger('useCommunityActions');
export const useCommunityActions = (visionaryGuild: Guild | undefined, operatorGuild: Guild | undefined) => {
	const {i18n} = useLingui();
	const [loadingRejoinCommunity, setLoadingRejoinCommunity] = useState(false);
	const [isCommunityMenuOpen, setIsCommunityMenuOpen] = useState(false);
	const communityButtonRef = useRef<HTMLButtonElement | null>(null);
	const hasVisionaryGuild = Boolean(visionaryGuild);
	const hasOperatorGuild = Boolean(operatorGuild);
	const getFirstViewableChannel = useCallback((guildId: string) => {
		const channels = Channels.getGuildChannels(guildId);
		return channels.find((channel) => GUILD_TEXT_BASED_CHANNEL_TYPES.has(channel.type));
	}, []);
	const handleRejoinCommunity = useCallback(
		async (type: 'visionary' | 'operator') => {
			setLoadingRejoinCommunity(true);
			try {
				if (type === 'visionary') {
					await PremiumCommands.rejoinVisionaryGuild();
				} else {
					await PremiumCommands.rejoinOperatorGuild();
				}
				const guild = type === 'visionary' ? visionaryGuild : operatorGuild;
				if (guild) {
					const firstChannel = getFirstViewableChannel(guild.id);
					ModalCommands.popAll();
					NavigationCommands.selectChannel(guild.id, firstChannel?.id);
				}
			} catch (error) {
				logger.error(`Failed to rejoin ${type} guild`, error);
				showRejoinCommunityErrorModal(error, type);
			} finally {
				setLoadingRejoinCommunity(false);
			}
		},
		[visionaryGuild, operatorGuild, getFirstViewableChannel],
	);
	const handleCommunityButtonPointerDown = useCallback((event: React.PointerEvent) => {
		const contextMenu = ContextMenu.contextMenu;
		const isOpen = !!contextMenu && contextMenu.target.target === communityButtonRef.current;
		if (isOpen) {
			event.stopPropagation();
			event.preventDefault();
			ContextMenuCommands.close();
		}
	}, []);
	const handleCommunityButtonClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			const contextMenu = ContextMenu.contextMenu;
			const isOpen = !!contextMenu && contextMenu.target.target === event.currentTarget;
			if (isOpen) {
				return;
			}
			ContextMenuCommands.openFromEvent(event, () => (
				<>
					<MenuItem
						onClick={() => {
							ContextMenuCommands.close();
							if (hasVisionaryGuild && visionaryGuild) {
								const firstChannel = getFirstViewableChannel(visionaryGuild.id);
								ModalCommands.popAll();
								NavigationCommands.selectChannel(visionaryGuild.id, firstChannel?.id);
							} else {
								handleRejoinCommunity('visionary');
							}
						}}
						data-flx="app.plutonium.use-community-actions.handle-community-button-click.menu-item.close"
					>
						{hasVisionaryGuild
							? i18n._(OPEN_VISIONARY_COMMUNITY_DESCRIPTOR)
							: i18n._(JOIN_VISIONARY_COMMUNITY_DESCRIPTOR)}
					</MenuItem>
					<MenuItem
						onClick={() => {
							ContextMenuCommands.close();
							if (hasOperatorGuild && operatorGuild) {
								const firstChannel = getFirstViewableChannel(operatorGuild.id);
								ModalCommands.popAll();
								NavigationCommands.selectChannel(operatorGuild.id, firstChannel?.id);
							} else {
								handleRejoinCommunity('operator');
							}
						}}
						data-flx="app.plutonium.use-community-actions.handle-community-button-click.menu-item.close--2"
					>
						{hasOperatorGuild
							? i18n._(OPEN_OPERATORS_COMMUNITY_DESCRIPTOR)
							: i18n._(JOIN_OPERATORS_COMMUNITY_DESCRIPTOR)}
					</MenuItem>
				</>
			));
		},
		[
			hasVisionaryGuild,
			hasOperatorGuild,
			visionaryGuild,
			operatorGuild,
			getFirstViewableChannel,
			handleRejoinCommunity,
			i18n,
		],
	);
	useEffect(() => {
		const handleContextMenuChange = () => {
			const contextMenu = ContextMenu.contextMenu;
			const isOpen =
				!!contextMenu && !!communityButtonRef.current && contextMenu.target.target === communityButtonRef.current;
			setIsCommunityMenuOpen(isOpen);
		};
		const disposer = autorun(handleContextMenuChange);
		return () => disposer();
	}, []);
	return {
		loadingRejoinCommunity,
		isCommunityMenuOpen,
		communityButtonRef,
		handleCommunityButtonPointerDown,
		handleCommunityButtonClick,
	};
};
